<script setup>
/* eslint-disable no-unused-vars */
/* global defineProps, defineEmits */
// import clockifyService from "@/api/services/clockify.service";
import TicketDepartmentTagService from "@/api/services/ticketDepartmentTag.service";
import TrackerService from "@/api/services/tracker.service";
import notificationService from "@/utils/plugins/NotificationService";
import SharedDialog from "@/components/shared/SharedDialog.vue";

import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from "vue";
import { describeRunningEntry, formatElapsed } from "@/views/internal-tickets/runningTimeEntries";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";


const props = defineProps({
  link: {
    type: String,
    required: true
  },
  trackernetProjectId: {
    type: String
  },
  // Departamento que gestiona el ticket y su tipo (IRI): de ellos salen las etiquetas de Trackernet
  departmentIri: {
    type: String,
    default: null
  },
  ticketTypeIri: {
    type: String,
    default: null
  },
  departmentName: {
    type: String,
    default: null
  },
  // Código del estado actual y asignado actual del formulario; el ticket recibido puede estar desfasado
  statusCode: {
    type: String,
    default: null
  },
  // Si el flujo permite pasar el ticket a Trabajando desde su estado, y cómo hacerlo (devuelve true si lo logra)
  canWork: {
    type: Boolean,
    default: false
  },
  ensureWorking: {
    type: Function,
    default: null
  },
  assignedEmployee: {
    type: [String, Object],
    default: null
  },
  ticket: {
    type: Object,
    required: true
  }
});

const authStore = useAuthStore();
const uiStore = useUiStore();
const user = computed(() => authStore.getUserLogged);

const instance = getCurrentInstance();

const timeEntryId = ref(null);

const showStartDialog = ref(false);

const currentEntry = ref(null);

const activeEntry = ref(null);

const showStopOldStartNew = ref(false);

const now = ref(Date.now());
let clockInterval = null;

const elapsed = computed(() => (timeEntryId.value && currentEntry.value ? formatElapsed(currentEntry.value.timeStart, now.value) : ''));
const activeEntryLines = computed(() => (activeEntry.value ? describeRunningEntry(activeEntry.value, props.ticket, now.value) : []));

const referenceOf = value => (value && typeof value === 'object' ? value['@id'] : value) || null;

const isAssignedUser = computed(() => {
  const assigned = referenceOf(props.assignedEmployee ?? props.ticket.assignedEmployee);
  return !!assigned && user.value?.['@id'] === assigned;
});

// Etiquetas de Trackernet del departamento del ticket (ticket_department_tags): una por tipo de ticket y la genérica "Ticket"
const departmentTags = ref([]);

const typeTag = computed(() => departmentTags.value.find(row => row.ticketType && row.ticketType === props.ticketTypeIri)?.timeEntryTag ?? null);
const genericTag = computed(() => departmentTags.value.find(row => !row.ticketType)?.timeEntryTag ?? null);

// Etiqueta que identifica el tipo del ticket: la de su tipo si el departamento la tiene, si no la genérica
const primaryTagId = computed(() => (typeTag.value ?? genericTag.value)?.['@id'] ?? null);

// La genérica es obligatoria: sin ella el departamento no está configurado y no se crea la entrada
const entryTagIds = computed(() => {
  if (!genericTag.value) {
    return [];
  }
  return [...new Set([typeTag.value?.['@id'], genericTag.value['@id']].filter(Boolean))];
});

let tagsLoading = Promise.resolve();

async function fetchDepartmentTags(departmentIri) {
  departmentTags.value = [];
  if (!departmentIri) return;
  try {
    const response = await TicketDepartmentTagService.getByDepartment(departmentIri);
    departmentTags.value = response?.data?.['hydra:member'] ?? [];
  } catch (error) {
    notificationService.error(error);
  }
}

watch(() => props.departmentIri, iri => { tagsLoading = fetchDepartmentTags(iri); }, { immediate: true });

const changedType = computed(() => {
  return currentEntry.value && !currentEntry.value.tags.some(tag => {
    return tag['@id'] === primaryTagId.value;
  });
});

const disabledStart = computed(() => {
  return (props.statusCode !== 'working' && !props.canWork) || !isAssignedUser.value;
});

// TimeEntry admite como máximo 8 horas
const MAX_ENTRY_MS = 8 * 60 * 60 * 1000;

function toggleShowDialog() {
  showStartDialog.value = !showStartDialog.value;
}

function toggleShowDialogRestart() {
  showStopOldStartNew.value = !showStopOldStartNew.value;
}

async function restartEntries() {
  await stop(activeEntry.value.id);
  activeEntry.value = null;
  toggleShowDialogRestart();
  await start();
}

async function start() {
  if (disabledStart.value) {
    notificationService.addNotification({
      type: 'error',
      message: 'Debes tener el ticket asignado y en un estado desde el que se pueda pasar a Trabajando para poder crear la entrada de tiempo.',
      duration: 10000,
      showClose: true
    });
    return;
  }
  if (activeEntry.value) {
    toggleShowDialogRestart();
    return;
  }
  await tagsLoading;
  if (!entryTagIds.value.length) {
    notificationService.addNotification({
      type: 'error',
      message: `No se puede arrancar el reloj: el departamento ${props.departmentName ? '"' + props.departmentName + '" ' : 'del ticket '}`
        + 'no tiene configurada la etiqueta de Trackernet para imputar horas a tickets. '
        + 'Ponte en contacto con Sistemas para que la creen y vuelve a intentarlo.',
      duration: 15000,
      showClose: true
    });
    return;
  }
  // Si el ticket no está en Trabajando (Abierto, En espera o Reabierto), se pasa a Trabajando antes de arrancar el reloj
  if (props.statusCode !== 'working' && !(await props.ensureWorking?.())) {
    return;
  }
  await TrackerService.createEntry({
    "billable": true,
    "description": '[' + props.ticket.tag + '] - ' + props.ticket.id + ' - ' + props.ticket.issue,
    "ticket": props.ticket['@id'],
    "project": props.trackernetProjectId,
    //"5dca96fd9fbfdb5c52836240"
    "timeStart": formatDate(new Date()),
    "tags": entryTagIds.value,
    // Add the tag id of the ticket type 'Tickets'
    "employee": user.value['@id']
  }).then(res => {
    if (res?.status === 201 || res?.status === 200) {
      timeEntryId.value = res.data.id;
      currentEntry.value = res.data;
    }
  }).catch(() => {});
}

async function updateEntry() {
  currentEntry.value.tagIds = entryTagIds.value;
  await TrackerService.updateEntry(currentEntry.value.id, {
    tags: currentEntry.value.tagIds
  });
  await stop();
}

async function stop(id = null) {
  uiStore.startLoading();
  try {
    const actualId = id ? id : timeEntryId.value;
    const response = await TrackerService.getCurrentEntry(actualId);
    const entry = response.data;
    const startedAt = new Date(response.data.timeStart);
    let endedAt = new Date();
    if (endedAt - startedAt > MAX_ENTRY_MS) {
      endedAt = new Date(startedAt.getTime() + MAX_ENTRY_MS);
      notificationService.addNotification({
        type: 'warning',
        message: 'La entrada de tiempo llevaba más de 8 horas en marcha; se ha parado registrando 8 horas, el máximo permitido.',
        duration: 10000,
        showClose: true
      });
    }
    entry.timeEnd = formatDate(endedAt);
    entry.timeStart = response.data.timeStart;
    entry.ticket = entry.ticket?.['@id'] ?? entry.ticket ?? null;
    entry.id = actualId;
    entry.employee = entry.employee['@id'];
    entry.project = entry.project['@id'];
    let tags = [];
    for (let i = 0; i < entry.tags.length; i++) {
      const element = entry.tags[i];
      tags.push(element['@id']);
    }
    entry.tags = tags;
    await TrackerService.stopEntry(entry);
    if (!id) {
      timeEntryId.value = null;
      currentEntry.value = null;
    }
  } finally {
    showStartDialog.value = false;
    uiStore.stopLoading();
  }
}

function formatDate(date) {
  const offset = date.getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hoursOfDay = String(date.getHours()).padStart(2, '0');
  const minutesOfDay = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hoursOfDay}:${minutesOfDay}:${seconds}${sign}${hours}:${minutes}`;
}

onUnmounted(() => clearInterval(clockInterval));

onMounted(async () => {
  clockInterval = setInterval(() => { now.value = Date.now(); }, 30000);
  if (!user.value?.id) return;
  try {
    // Un ticket tiene 0..n entradas; si el usuario tiene alguna abierta en este ticket, el reloj está en marcha
    const own = await TrackerService.getEmployeeEntries(user.value.id, {
      'ticket.id': props.ticket.id, 'exists[timeEnd]': false, 'order[timeStart]': 'DESC', itemsPerPage: 1
    });
    const ownEntry = own?.data?.['hydra:member']?.[0];
    if (ownEntry) {
      timeEntryId.value = ownEntry.id;
      currentEntry.value = ownEntry;
      return;
    }
    const latest = await TrackerService.getEmployeeEntries(user.value.id, { 'order[timeStart]': 'DESC', itemsPerPage: 1 });
    const latestEntry = latest?.data?.['hydra:member']?.[0];
    activeEntry.value = latestEntry && !latestEntry.timeEnd ? latestEntry : null;
  } catch (error) {
    notificationService.error(error);
  }
});
</script>

<template>
  <div class="clocky-container">
    <button type="button" class="tn-card" :class="{ 'tn-card--running': timeEntryId }"
      :aria-label="timeEntryId ? 'Trackernet: en marcha ' + elapsed + '. Parar reloj' : 'Trackernet: parado. Arrancar reloj'"
      @click="!timeEntryId ? start() : (changedType ? toggleShowDialog() : stop())">
      <img class="tn-logo" src="@/assets/icons/tracker-net-blue.svg" alt="trackernet">
      <span class="tn-text">
        <span class="tn-title">Trackernet</span>
        <span v-if="timeEntryId" class="tn-status tn-status--running">
          <span class="tn-dot" />{{ elapsed || 'En marcha' }}
        </span>
        <span v-else class="tn-status">Parado</span>
      </span>
      <img v-if="!timeEntryId" class="tn-action" src="@/assets/icons/play-blue.svg" alt="Arrancar reloj">
      <img v-else class="tn-action" src="@/assets/icons/stop-red.svg" alt="Parar reloj">
    </button>
    <SharedDialog v-if="showStartDialog" title="La entrada de tiempo en curso tiene una etiqueta diferente al actual
           que se le ha asignado al ticket según su tipo" :close-icon="true"
      confirm-text="Cambiar etiqueta y parar reloj" decline-text="Solo parar reloj"
      message="¿Deseas cambiar la etiqueta de la entrada de tiempo en curso a la del tipo actual del ticket?"
      @confirm="updateEntry()" @close="toggleShowDialog()" @decline="stop()" />
    <SharedDialog v-if="showStopOldStartNew" title="Hay una tarea en Trackernet en marcha, ¿deseas pararla?" :close-icon="true"
      confirm-text="Parar la tarea e iniciar tarea en Trackernet" decline-text="Cancelar"
      message="Esta es la tarea que está en marcha:"
      @confirm="restartEntries()" @close="toggleShowDialogRestart()" @decline="toggleShowDialogRestart()">
      <ul class="running-entry-details">
        <li v-for="line in activeEntryLines" :key="line" :class="{ warning: line.startsWith('No corresponde') }">{{ line }}</li>
      </ul>
    </SharedDialog>
  </div>
</template>

<style scoped lang="scss">
.clocky-container {
  width: 100%;
  max-width: 360px;
  font-family: Montserrat, sans-serif;
}

.tn-card {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 85px;
  padding: 12px 14px;
  border: 2px solid transparent;
  border-radius: 16px;
  background-color: white;
  color: #474CE6;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #474CE6;
    box-shadow: 0 4px 14px rgba(71, 76, 230, 0.18);
  }

  &:focus-visible {
    outline: 3px solid rgba(71, 76, 230, 0.4);
    outline-offset: 2px;
  }

  &--running {
    border-color: #1f9d55;

    &:hover {
      border-color: #dc3545;
      box-shadow: 0 4px 14px rgba(220, 53, 69, 0.18);
    }
  }
}

.tn-logo {
  flex: none;
  width: 38px;
  height: 42px;
}

.tn-text {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.tn-title {
  font-size: 19px;
  font-weight: 800;
  line-height: 24px;
  letter-spacing: -0.02em;
}

.tn-status {
  font-size: 15px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
  color: #6c757d;

  &--running {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #1f9d55;
  }
}

.tn-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #1f9d55;
  animation: tn-pulse 1.4s ease-in-out infinite;
}

.tn-action {
  flex: none;
  width: 40px;
  height: 40px;
}

@keyframes tn-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.running-entry-details {
  margin: 0.5rem 0 0;
  padding-left: 1.2rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5rem;
  text-align: left;

  .warning {
    color: #dc3545;
    font-weight: 700;
  }
}
</style>
